import Button from '@/components/core/Button';

import Modal from '@/se/components/Modal';
import { format } from 'date-fns';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { FilePicker } from 'react-file-picker';
import { withRouter } from 'react-router';
import { withProps } from 'recompose';
import { Formats, getMediaTypes } from './formats';
import { PhysicianUploadConfirmation } from './PhysicianUploadConfirmation';
import { ScheduleUploadConfirmation } from './ScheduleUploadConfirmation';
import Uploader, { UPLOAD_RESOURCE } from './Uploader';

const DefaultFormat = [Formats.CSV];
const DefaultMediaType = getMediaTypes([Formats.CSV]);

const useAssertUnchanged = (value, name) => {
  const original = useMemo(() => value, [value]);

  useEffect(() => {
    if (value !== original) {
      console.warn(name, 'should not change through rerenders.', 'It changed to', value, 'from', original);
    }
  }, [value, original, name]);
};

const UploadButton = withRouter(
  ({ formats, history, location, UploadConfirmation, resource, title, className, ButtonComponent, ...props }) => {
    useAssertUnchanged(formats, 'UploadButton formats prop');
    useAssertUnchanged(history, 'UploadButton history prop');
    useAssertUnchanged(resource, 'UploadButton resource prop');
    useAssertUnchanged(ButtonComponent, 'UploadButton ButtonComponent prop');

    const [file, setFile] = useState(null);

    // filePicker() is rendered twice: the main picker below, and the "Change File" picker
    // rendered inside the confirmation modal once a file is selected. Both instances used to
    // share a single object ref, so whichever <div> committed last silently clobbered the
    // other's reference and only one input ever got the accept attribute. A callback ref fires
    // once per mounted node (no clobbering), so use it to apply accept directly at mount time
    // for every picker instance.
    const applyAccept = useCallback(
      node => {
        if (!node) {
          return;
        }

        const input = node.querySelector('input');
        if (input) {
          input.setAttribute('accept', formats ? getMediaTypes(formats) : DefaultMediaType);
        }
      },
      [formats]
    );

    const handleChange = useCallback(
      file => {
        setFile(file);
      },
      [setFile]
    );

    const handleError = useCallback(
      message => {
        setFile(null);
        // react-file-picker's onError already passes a real message (unsupported extension vs
        // file too large) - surface that instead of a hardcoded, format-specific sentence.
        alert(message || 'Unable to select this file.');
      },
      [setFile]
    );

    const filePicker = useCallback(
      text => (
        <div ref={applyAccept}>
          <FilePicker
            extensions={formats ? formats : DefaultFormat}
            // Default is 2 (2,000,000 bytes); align with the parse-service Lambda's ~4.5 MB
            // effective PDF payload ceiling.
            maxSize={5}
            onChange={handleChange}
            onError={handleError}
          >
            {ButtonComponent || (
              <Button className={className} {...props}>
                {text}
              </Button>
            )}
          </FilePicker>
        </div>
      ),
      [formats, handleChange, handleError, ButtonComponent, className, props, applyAccept]
    );

    const handleCancel = useCallback(() => setFile(null), [setFile]);

    const handleDone = useCallback(
      date => {
        setFile(null);
        if (history) {
          history.replace(`${location.pathname}?date=${format(date, 'YYYY-MM-DD')}`);
        }
      },
      [history, location.pathname]
    );

    return (
      <Fragment>
        {filePicker(title)}
        {file && (
          <Modal title={title} withRouter={false} onClose={handleCancel}>
            <Uploader
              filePicker={filePicker}
              file={file}
              onCancel={handleCancel}
              resource={resource}
              UploadConfirmation={UploadConfirmation}
              onDone={handleDone}
            />
          </Modal>
        )}
      </Fragment>
    );
  }
);

export const ScheduleUploadButton = withProps({
  resource: UPLOAD_RESOURCE.SCHEDULE,
  title: 'Upload Schedule',
  UploadConfirmation: ScheduleUploadConfirmation,
})(UploadButton);

export const PhysicianUploadButton = withProps({
  resource: UPLOAD_RESOURCE.PHYSICIAN,
  title: 'Upload Physicians',
  UploadConfirmation: PhysicianUploadConfirmation,
})(UploadButton);

export default UploadButton;
